import React, { useCallback, useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import Layout from "../../layout/Layout";
import { useAuth } from "../../context/AuthContext";

import {
  getMonthlyMealMenu,
  saveMonthlyMealMenuDay,
  deleteMonthlyMealMenuDay,
  copyMonthlyMealMenu,
} from "../../../api/satinalma/satinalmaApi";

const MONTHS = [
  "Ocak",
  "Şubat",
  "Mart",
  "Nisan",
  "Mayıs",
  "Haziran",
  "Temmuz",
  "Ağustos",
  "Eylül",
  "Ekim",
  "Kasım",
  "Aralık",
];

const WEEK_DAYS = [
  "Pazartesi",
  "Salı",
  "Çarşamba",
  "Perşembe",
  "Cuma",
  "Cumartesi",
  "Pazar",
];

function MonthlyMealMenuPage() {
  const { user } = useAuth();

  const today = new Date();

  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  /*
  |--------------------------------------------------------------------------
  | PERMISSION
  |--------------------------------------------------------------------------
  */

  const permissions = user?.permissions || [];

  const canManage = permissions.some((permission) => {
    if (typeof permission === "string") {
      return permission === "monthlyMealMenu_manage";
    }

    return (
      permission?.name === "monthlyMealMenu_manage" ||
      permission?.permission === "monthlyMealMenu_manage"
    );
  });

  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(currentMonth);

  const [days, setDays] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [showMenuModal, setShowMenuModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);

  const [selectedDay, setSelectedDay] = useState(null);

  const [form, setForm] = useState({
    meal1: "",
    meal2: "",
    meal3: "",
    meal4: "",
    note: "",
  });

  const [copyForm, setCopyForm] = useState({
    sourceYear: currentYear,
    sourceMonth: currentMonth,
  });

  /*
  |--------------------------------------------------------------------------
  | AYLIK MENÜ
  |--------------------------------------------------------------------------
  */

  const fetchMenu = useCallback(async () => {
    try {
      setLoading(true);

      const response = await getMonthlyMealMenu(year, month);

      setDays(response?.data?.days || []);
    } catch (error) {
      console.error("Menü getirme hatası:", error);

      alert(error?.response?.data?.message || "Menü bilgileri alınamadı.");
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  /*
  |--------------------------------------------------------------------------
  | GÜN MENÜSÜ
  |--------------------------------------------------------------------------
  */

  const getDayMenu = (day) => {
    return days.find((item) => Number(item.day) === Number(day));
  };

  /*
  |--------------------------------------------------------------------------
  | GÜNE TIKLA
  |--------------------------------------------------------------------------
  */

  const handleDayClick = (day) => {
    if (!canManage) return;

    const menu = getDayMenu(day);

    setSelectedDay(day);

    if (menu) {
      setForm({
        meal1: menu.meal1 || "",
        meal2: menu.meal2 || "",
        meal3: menu.meal3 || "",
        meal4: menu.meal4 || "",
        note: menu.note || "",
      });
    } else {
      setForm({
        meal1: "",
        meal2: "",
        meal3: "",
        meal4: "",
        note: "",
      });
    }

    setShowMenuModal(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /*
  |--------------------------------------------------------------------------
  | KAYDET
  |--------------------------------------------------------------------------
  */

  const handleSave = async () => {
    if (!canManage) return;

    try {
      if (
        !form.meal1.trim() ||
        !form.meal2.trim() ||
        !form.meal3.trim() ||
        !form.meal4.trim()
      ) {
        alert("4 yemek alanını da doldurmalısınız.");
        return;
      }

      setSaving(true);

      await saveMonthlyMealMenuDay({
        year,
        month,
        day: selectedDay,
        meal1: form.meal1,
        meal2: form.meal2,
        meal3: form.meal3,
        meal4: form.meal4,
        note: form.note,
      });

      setShowMenuModal(false);

      await fetchMenu();
    } catch (error) {
      console.error("Menü kaydetme hatası:", error);

      alert(error?.response?.data?.message || "Menü kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | SİL
  |--------------------------------------------------------------------------
  */

  const handleDelete = async () => {
    if (!canManage) return;

    const menu = getDayMenu(selectedDay);

    if (!menu) return;

    const confirmed = window.confirm(
      `${selectedDay} ${MONTHS[month - 1]} tarihindeki menü silinsin mi?`
    );

    if (!confirmed) return;

    try {
      setSaving(true);

      await deleteMonthlyMealMenuDay({
        year,
        month,
        day: selectedDay,
      });

      setShowMenuModal(false);

      await fetchMenu();
    } catch (error) {
      console.error("Menü silme hatası:", error);

      alert(error?.response?.data?.message || "Menü silinemedi.");
    } finally {
      setSaving(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | AY KOPYALA
  |--------------------------------------------------------------------------
  */

  const handleCopyMonth = async () => {
    if (!canManage) return;

    if (
      Number(copyForm.sourceYear) === Number(year) &&
      Number(copyForm.sourceMonth) === Number(month)
    ) {
      alert("Kaynak ay ile hedef ay aynı olamaz.");
      return;
    }

    const confirmed = window.confirm(
      `${MONTHS[copyForm.sourceMonth - 1]} ${copyForm.sourceYear} menüsü, ${
        MONTHS[month - 1]
      } ${year} ayına kopyalanacak.

Hedef ayda kayıt varsa mevcut menülerin üzerine yazılacak.

Devam edilsin mi?`
    );

    if (!confirmed) return;

    try {
      setSaving(true);

      await copyMonthlyMealMenu({
        sourceYear: Number(copyForm.sourceYear),
        sourceMonth: Number(copyForm.sourceMonth),
        targetYear: Number(year),
        targetMonth: Number(month),
      });

      setShowCopyModal(false);

      await fetchMenu();
    } catch (error) {
      console.error("Menü kopyalama hatası:", error);

      alert(error?.response?.data?.message || "Menü kopyalanamadı.");
    } finally {
      setSaving(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | EXCEL AKTAR
  |--------------------------------------------------------------------------
  */

  const handleExportExcel = async () => {
    if (!days.length) {
      alert("Seçili ay için dışa aktarılacak yemek menüsü bulunmuyor.");
      return;
    }

    try {
      setExporting(true);

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Göktaş Metal Kurumsal Hafıza";
      workbook.created = new Date();

      const worksheet = workbook.addWorksheet("Aylık Yemek Menüsü", {
        properties: { defaultRowHeight: 22 },
        views: [{ state: "frozen", ySplit: 4, showGridLines: false }],
        pageSetup: {
          paperSize: 9,
          orientation: "landscape",
          fitToPage: true,
          fitToWidth: 1,
          fitToHeight: 0,
          margins: {
            left: 0.25,
            right: 0.25,
            top: 0.5,
            bottom: 0.5,
            header: 0.2,
            footer: 0.2,
          },
        },
      });

      worksheet.columns = [
        { key: "date", width: 14 },
        { key: "weekDay", width: 15 },
        { key: "meal1", width: 27 },
        { key: "meal2", width: 27 },
        { key: "meal3", width: 27 },
        { key: "meal4", width: 27 },
        { key: "note", width: 32 },
      ];

      worksheet.mergeCells("A1:G1");
      worksheet.getCell("A1").value = "GÖKTAŞ METAL AYLIK YEMEK MENÜSÜ";
      worksheet.getCell("A1").font = {
        name: "Arial",
        size: 16,
        bold: true,
        color: { argb: "FFFFFFFF" },
      };
      worksheet.getCell("A1").fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF17365D" },
      };
      worksheet.getCell("A1").alignment = {
        horizontal: "left",
        vertical: "middle",
      };
      worksheet.getRow(1).height = 32;

      worksheet.mergeCells("A2:G2");
      worksheet.getCell("A2").value = `${MONTHS[month - 1]} ${year}`;
      worksheet.getCell("A2").font = {
        name: "Arial",
        size: 11,
        bold: true,
        color: { argb: "FF31465D" },
      };
      worksheet.getCell("A2").alignment = {
        horizontal: "left",
        vertical: "middle",
      };
      worksheet.getRow(2).height = 24;

      const populatedDayCount = days.filter(
        (item) => item.meal1 || item.meal2 || item.meal3 || item.meal4
      ).length;
      worksheet.mergeCells("A3:G3");
      worksheet.getCell("A3").value = `Menü girilen gün: ${populatedDayCount} / ${daysInMonth}`;
      worksheet.getCell("A3").font = {
        name: "Arial",
        size: 10,
        italic: true,
        color: { argb: "FF66758A" },
      };
      worksheet.getCell("A3").alignment = { vertical: "middle" };

      const headerRow = worksheet.getRow(4);
      headerRow.values = [
        "Tarih",
        "Gün",
        "1. Yemek",
        "2. Yemek",
        "3. Yemek",
        "4. Yemek",
        "Not",
      ];
      headerRow.height = 28;
      headerRow.eachCell((cell) => {
        cell.font = {
          name: "Arial",
          size: 10,
          bold: true,
          color: { argb: "FFFFFFFF" },
        };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF2F6F98" },
        };
        cell.alignment = { horizontal: "center", vertical: "middle" };
        cell.border = {
          right: { style: "thin", color: { argb: "FFFFFFFF" } },
          bottom: { style: "thin", color: { argb: "FFB8C7D4" } },
        };
      });

      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month - 1, day);
        const menu = getDayMenu(day);
        const row = worksheet.addRow({
          date,
          weekDay: WEEK_DAYS[(date.getDay() + 6) % 7],
          meal1: menu?.meal1 || "",
          meal2: menu?.meal2 || "",
          meal3: menu?.meal3 || "",
          meal4: menu?.meal4 || "",
          note: menu?.note || "",
        });

        const isWeekend = date.getDay() === 0 || date.getDay() === 6;
        row.height = 38;
        row.eachCell({ includeEmpty: true }, (cell, columnNumber) => {
          cell.font = { name: "Arial", size: 10, color: { argb: "FF172B43" } };
          cell.alignment = {
            horizontal: columnNumber <= 2 ? "center" : "left",
            vertical: "middle",
            wrapText: true,
          };
          cell.border = {
            bottom: { style: "thin", color: { argb: "FFDDE5EC" } },
          };
          if (isWeekend) {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFF4F6F8" },
            };
          }
        });
      }

      worksheet.getColumn("date").numFmt = "dd.mm.yyyy";
      worksheet.autoFilter = { from: "A4", to: "G4" };
      worksheet.headerFooter.oddFooter =
        "&LGöktaş Metal Kurumsal Hafıza&R&S Sayfa &P / &N";

      const buffer = await workbook.xlsx.writeBuffer();
      const fileName = `Yemek_Menusu_${year}_${String(month).padStart(2, "0")}.xlsx`;
      saveAs(
        new Blob([buffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        fileName
      );
    } catch (error) {
      console.error("Yemek menüsü Excel export hatası:", error);
      alert("Excel dosyası oluşturulamadı.");
    } finally {
      setExporting(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | CALENDAR
  |--------------------------------------------------------------------------
  */

  const daysInMonth = new Date(year, month, 0).getDate();

  const firstDay = new Date(year, month - 1, 1).getDay();

  const firstDayOffset = firstDay === 0 ? 6 : firstDay - 1;

  const calendarCells = [];

  for (let i = 0; i < firstDayOffset; i++) {
    calendarCells.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    calendarCells.push(day);
  }

  const yearOptions = [
    currentYear - 2,
    currentYear - 1,
    currentYear,
    currentYear + 1,
    currentYear + 2,
  ];

  return (
    <Layout>
      <div className="p-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
          <div>
            <h2 className="fw-bold mb-1">Yemek Menüsü</h2>

            <div className="text-muted">
              {canManage
                ? "Aylık yemek planlama ve düzenleme"
                : `${MONTHS[currentMonth - 1]} ${currentYear} yemek menüsü`}
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2">
            {canManage && (
              <>
                <Form.Select
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                  style={{ width: 150 }}
                >
                  {MONTHS.map((monthName, index) => (
                    <option key={index} value={index + 1}>
                      {monthName}
                    </option>
                  ))}
                </Form.Select>

                <Form.Select
                  value={year}
                  onChange={(e) => setYear(Number(e.target.value))}
                  style={{ width: 110 }}
                >
                  {yearOptions.map((yearItem) => (
                    <option key={yearItem} value={yearItem}>
                      {yearItem}
                    </option>
                  ))}
                </Form.Select>

                <Button
                  variant="outline-primary"
                  onClick={() => setShowCopyModal(true)}
                >
                  Geçmiş Menüden Kopyala
                </Button>
              </>
            )}

            <Button
              variant="success"
              onClick={handleExportExcel}
              disabled={loading || exporting || days.length === 0}
            >
              {exporting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Excel hazırlanıyor
                </>
              ) : (
                <>
                  <i className="bi bi-file-earmark-excel me-2" />
                  Excel'e Aktar
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="mb-3">
          <h4 className="fw-bold mb-0">
            {MONTHS[month - 1]} {year}
          </h4>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <div
              className="mb-2"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, minmax(140px, 1fr))",
                gap: 8,
                minWidth: 1050,
              }}
            >
              {WEEK_DAYS.map((day) => (
                <div key={day} className="text-center fw-bold text-muted py-2">
                  {day}
                </div>
              ))}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7, minmax(140px, 1fr))",
                gap: 8,
                minWidth: 1050,
              }}
            >
              {calendarCells.map((day, index) => {
                if (!day) {
                  return (
                    <div
                      key={`empty-${index}`}
                      style={{
                        minHeight: 180,
                        background: "#f8f9fa",
                        borderRadius: 8,
                      }}
                    />
                  );
                }

                const menu = getDayMenu(day);

                const date = new Date(year, month - 1, day);

                const isToday = date.toDateString() === today.toDateString();

                const isWeekend = date.getDay() === 0 || date.getDay() === 6;

                return (
                  <div
                    key={day}
                    onClick={() => handleDayClick(day)}
                    className="border rounded p-3 position-relative"
                    style={{
                      minHeight: 180,
                      cursor: canManage ? "pointer" : "default",
                      background: isToday
                        ? "#f0f7ff"
                        : isWeekend
                          ? "#fafafa"
                          : "#fff",
                      borderColor: isToday ? "#0d6efd" : undefined,
                      transition: "all 0.15s ease",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div
                        className={isToday ? "fw-bold text-primary" : "fw-bold"}
                        style={{ fontSize: 18 }}
                      >
                        {day}
                      </div>

                      {menu && (
                        <span className="badge bg-success">Menü Var</span>
                      )}
                    </div>

                    {menu ? (
                      <div>
                        <div className="small mb-2">
                          <span className="text-muted me-1">1.</span>
                          {menu.meal1}
                        </div>

                        <div className="small mb-2">
                          <span className="text-muted me-1">2.</span>
                          {menu.meal2}
                        </div>

                        <div className="small mb-2">
                          <span className="text-muted me-1">3.</span>
                          {menu.meal3}
                        </div>

                        <div className="small mb-2">
                          <span className="text-muted me-1">4.</span>
                          {menu.meal4}
                        </div>

                        {menu.note && (
                          <div className="small text-muted border-top pt-2 mt-2">
                            {menu.note}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-muted small text-center mt-5">
                        {canManage
                          ? "Menü eklemek için tıklayın"
                          : "Menü bulunmuyor"}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {canManage && (
          <Modal
            show={showMenuModal}
            onHide={() => !saving && setShowMenuModal(false)}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {selectedDay} {MONTHS[month - 1]} {year}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">1. Yemek</Form.Label>

                <Form.Control
                  name="meal1"
                  value={form.meal1}
                  onChange={handleChange}
                  placeholder="1. yemek"
                  autoFocus
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">2. Yemek</Form.Label>

                <Form.Control
                  name="meal2"
                  value={form.meal2}
                  onChange={handleChange}
                  placeholder="2. yemek"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">3. Yemek</Form.Label>

                <Form.Control
                  name="meal3"
                  value={form.meal3}
                  onChange={handleChange}
                  placeholder="3. yemek"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">4. Yemek</Form.Label>

                <Form.Control
                  name="meal4"
                  value={form.meal4}
                  onChange={handleChange}
                  placeholder="4. yemek"
                />
              </Form.Group>

              <Form.Group>
                <Form.Label>Not</Form.Label>

                <Form.Control
                  as="textarea"
                  rows={2}
                  name="note"
                  value={form.note}
                  onChange={handleChange}
                  placeholder="İsteğe bağlı"
                />
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              {getDayMenu(selectedDay) && (
                <Button
                  variant="outline-danger"
                  onClick={handleDelete}
                  disabled={saving}
                  className="me-auto"
                >
                  Sil
                </Button>
              )}

              <Button
                variant="secondary"
                onClick={() => setShowMenuModal(false)}
                disabled={saving}
              >
                Vazgeç
              </Button>

              <Button variant="primary" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Kaydediliyor
                  </>
                ) : getDayMenu(selectedDay) ? (
                  "Güncelle"
                ) : (
                  "Kaydet"
                )}
              </Button>
            </Modal.Footer>
          </Modal>
        )}

        {canManage && (
          <Modal
            show={showCopyModal}
            onHide={() => !saving && setShowCopyModal(false)}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Geçmiş Menüden Kopyala</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <div className="mb-4">
                <div className="text-muted small mb-1">Hedef Ay</div>

                <div className="fw-bold">
                  {MONTHS[month - 1]} {year}
                </div>
              </div>

              <hr />

              <div className="fw-bold mb-3">Kaynak menüyü seçin</div>

              <div className="row">
                <div className="col-md-7">
                  <Form.Group>
                    <Form.Label>Ay</Form.Label>

                    <Form.Select
                      value={copyForm.sourceMonth}
                      onChange={(e) =>
                        setCopyForm((prev) => ({
                          ...prev,
                          sourceMonth: Number(e.target.value),
                        }))
                      }
                    >
                      {MONTHS.map((monthName, index) => (
                        <option key={index} value={index + 1}>
                          {monthName}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>

                <div className="col-md-5">
                  <Form.Group>
                    <Form.Label>Yıl</Form.Label>

                    <Form.Select
                      value={copyForm.sourceYear}
                      onChange={(e) =>
                        setCopyForm((prev) => ({
                          ...prev,
                          sourceYear: Number(e.target.value),
                        }))
                      }
                    >
                      {yearOptions.map((yearItem) => (
                        <option key={yearItem} value={yearItem}>
                          {yearItem}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>

              <div className="alert alert-warning mt-4 mb-0">
                Hedef ayda mevcut menü varsa üzerine yazılacaktır.
              </div>
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowCopyModal(false)}
                disabled={saving}
              >
                Vazgeç
              </Button>

              <Button
                variant="primary"
                onClick={handleCopyMonth}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Kopyalanıyor
                  </>
                ) : (
                  "Menüyü Kopyala"
                )}
              </Button>
            </Modal.Footer>
          </Modal>
        )}
      </div>
    </Layout>
  );
}

export default MonthlyMealMenuPage;
